import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import LandingPage from './pages/LandingPage';
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import OAuthCallback from './components/auth/OAuthCallback';
import Dashboard from './components/dashboard/Dashboard';
import Practice from './pages/Practice';
import Survival from './pages/Survival';
import Rush from './pages/Rush';
import QuestionLibrary from './pages/QuestionLibrary';
import TagPractice from './pages/TagPractice';
import StatsAndRankings from './pages/StatsAndRankings';
import SkillPractice from './pages/SkillPractice';
import Mingu from './pages/Mingu';
import { MINGU_ENABLED } from './config/features';
import Subscription from './pages/Subscription';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import SubscriptionCancel from './pages/SubscriptionCancel';
import AccountSettings from './pages/AccountSettings';
import Support from './pages/Support';
import Admin from './pages/Admin';
import AdminUsers from './pages/AdminUsers';
import AdminHealth from './pages/AdminHealth';
import ProtectedRoute from './components/ProtectedRoute';
import SubscriptionRoute from './components/SubscriptionRoute';
import AdminRoute from './components/AdminRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { SubjectProvider, SubjectScope } from './subjects/SubjectContext';
import AddSubject from './subjects/AddSubject';

const Providers: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <AuthProvider>
    <SubjectProvider>{children}</SubjectProvider>
  </AuthProvider>
);

const HomeRoute: React.FC = () => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <LandingPage />;
};

function App() {
  return (
    <ErrorBoundary>
      <Providers>
        <Router>
          <SubjectScope>
            <Routes>
              <Route path="/login" element={<LoginForm />} />
              <Route path="/register" element={<RegisterForm />} />
              <Route path="/auth/callback" element={<OAuthCallback />} />

              {/* Subscription routes */}
              <Route
                path="/subscription"
                element={
                  <ErrorBoundary>
                    <Subscription />
                  </ErrorBoundary>
                }
              />
              <Route path="/subscription/success" element={<SubscriptionSuccess />} />
              <Route path="/subscription/cancel" element={<SubscriptionCancel />} />

              <Route
                path="/dashboard"
                element={
                  <SubscriptionRoute allowFreeAccess>
                    <ErrorBoundary>
                      <Dashboard />
                    </ErrorBoundary>
                  </SubscriptionRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <AccountSettings />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/support"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <Support />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/practice"
                element={
                  <SubscriptionRoute allowFreeAccess>
                    <ErrorBoundary>
                      <Practice />
                    </ErrorBoundary>
                  </SubscriptionRoute>
                }
              />
              <Route
                path="/practice/tags"
                element={
                  <SubscriptionRoute allowFreeAccess>
                    <ErrorBoundary>
                      <TagPractice />
                    </ErrorBoundary>
                  </SubscriptionRoute>
                }
              />
              <Route
                path="/survival"
                element={
                  <SubscriptionRoute allowFreeAccess>
                    <ErrorBoundary>
                      <Survival />
                    </ErrorBoundary>
                  </SubscriptionRoute>
                }
              />
              <Route
                path="/rush"
                element={
                  <SubscriptionRoute allowFreeAccess>
                    <ErrorBoundary>
                      <Rush />
                    </ErrorBoundary>
                  </SubscriptionRoute>
                }
              />
              <Route
                path="/library"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <QuestionLibrary />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/library/:year/:questionNumber"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <QuestionLibrary />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subjects/add"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <AddSubject />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/stats"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <StatsAndRankings />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mingu"
                element={
                  MINGU_ENABLED ? (
                    <SubscriptionRoute allowFreeAccess>
                      <ErrorBoundary>
                        <Mingu />
                      </ErrorBoundary>
                    </SubscriptionRoute>
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                }
              />
              <Route
                path="/skills/:skill"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <SkillPractice />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              {/* Redirect old leaderboard route to new stats page */}
              <Route
                path="/leaderboard"
                element={<Navigate to="/stats" replace />}
              />
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <ErrorBoundary>
                      <Admin />
                    </ErrorBoundary>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <AdminRoute>
                    <ErrorBoundary>
                      <AdminUsers />
                    </ErrorBoundary>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/server-health"
                element={
                  <AdminRoute>
                    <ErrorBoundary>
                      <AdminHealth />
                    </ErrorBoundary>
                  </AdminRoute>
                }
              />
              <Route path="/" element={<HomeRoute />} />
            </Routes>
          </SubjectScope>
        </Router>
      </Providers>
    </ErrorBoundary>
  );
}

export default App;
